import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  listTestimonianze,
  createTestimonianza,
  updateTestimonianza,
  deleteTestimonianza,
} from "@/lib/api";

const EMPTY = {
  nome: "",
  citta: "",
  testo: "",
  rating: 5,
  data_recensione: new Date().toISOString().slice(0, 10),
  sort_order: 0,
};

export default function TestimonianzePanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => {
    setLoading(true);
    setError("");
    listTestimonianze()
      .then(setItems)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      nome: t.nome || "",
      citta: t.citta || "",
      testo: t.testo || "",
      rating: t.rating || 5,
      data_recensione: t.data_recensione || new Date().toISOString().slice(0, 10),
      sort_order: t.sort_order || 0,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await updateTestimonianza(editing.id, form);
      } else {
        await createTestimonianza(form);
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      setError(err.message || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTestimonianza(deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch (err) {
      setError(err.message || "Errore eliminazione");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#0B1E36]">Testimonianze</h2>
          <p className="text-sm text-[#64748B]">
            {items.length} {items.length === 1 ? "recensione" : "recensioni"}
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#0B1E36] hover:bg-[#0B1E36]/90"
          data-testid="testimonianze-create-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuova testimonianza
        </Button>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-[#0B1E36]" />
        </div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center text-[#64748B]">
          Nessuna testimonianza ancora.
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((t) => (
            <Card key={t.id} className="p-5">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-bold text-[#0B1E36]">{t.nome}</h3>
                  {t.citta && (
                    <p className="text-sm text-[#64748B]">{t.citta}</p>
                  )}
                </div>
                {t.rating && (
                  <div className="flex gap-0.5 text-yellow-500">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-current" />
                    ))}
                  </div>
                )}
              </div>
              <p className="text-sm text-[#334155] mb-3 line-clamp-4">"{t.testo}"</p>
              {t.data_recensione && (
                <p className="text-xs text-[#94A3B8] mb-3">{t.data_recensione}</p>
              )}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEdit(t)}
                  className="flex-1"
                  data-testid={`testimonianza-edit-${t.id}`}
                >
                  <Pencil className="w-3 h-3 mr-1" />
                  Modifica
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDeleteTarget(t)}
                  className="text-red-600 hover:bg-red-50 hover:text-red-700"
                  data-testid={`testimonianza-delete-${t.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog create/edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Modifica testimonianza" : "Nuova testimonianza"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="nome">Nome *</Label>
                <Input
                  id="nome"
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  required
                  data-testid="testimonianza-form-nome"
                />
              </div>
              <div>
                <Label htmlFor="citta">Città</Label>
                <Input
                  id="citta"
                  value={form.citta}
                  onChange={(e) => setForm({ ...form, citta: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="testo">Testo recensione *</Label>
              <Textarea
                id="testo"
                value={form.testo}
                onChange={(e) => setForm({ ...form, testo: e.target.value })}
                rows={4}
                required
                data-testid="testimonianza-form-testo"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Rating</Label>
                <div className="flex gap-1 mt-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setForm({ ...form, rating: n })}
                      className="p-1"
                      aria-label={`${n} stelle`}
                    >
                      <Star
                        className={`w-6 h-6 transition-colors ${
                          n <= form.rating
                            ? "fill-yellow-500 text-yellow-500"
                            : "text-gray-300"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="data">Data recensione</Label>
                <Input
                  id="data"
                  type="date"
                  value={form.data_recensione}
                  onChange={(e) => setForm({ ...form, data_recensione: e.target.value })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={saving}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                className="bg-[#0B1E36] hover:bg-[#0B1E36]/90"
                disabled={saving}
                data-testid="testimonianza-form-save"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvataggio…
                  </>
                ) : editing ? (
                  "Salva modifiche"
                ) : (
                  "Crea"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Eliminare la testimonianza di "{deleteTarget?.nome}"?
            </AlertDialogTitle>
            <AlertDialogDescription>L'operazione è irreversibile.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
